import { TaskHandoffButton } from "../components/TaskHandoffButton"
import { TaskHandoffHistory } from "../components/sessions/TaskHandoffHistory"
import NiceModal from "@ebay/nice-modal-react"
import { observer } from "mobx-react"
import {
    type MutableRefObject,
    type ReactNode,
    type RefObject,
    type UIEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { TaskNavbarSlot } from "../layout/CodeAppLayout"
import { EventLog } from "../components/EventLog"
import { ImageDropOverlay } from "../components/ImageDropOverlay"
import { InputBar } from "../components/InputBar"
import { GuestComposerNotice } from "../components/computers/GuestBanner"
import { SessionThreadView } from "../components/SessionThreadView"
import { taskWorktreeControlForModel } from "../components/TaskGitControl"
import { TaskWorkspaceSidebar } from "../components/TaskWorkspaceSidebar"
import { SettingsModal } from "../components/settings/SettingsModal"
import { ThreadActivityBar, isActiveThreadStatus } from "../components/thread/ThreadActivityBar"
import { TaskThreadViewport, THREAD_VIEWPORT_LIVE_EDGE_THRESHOLD_PX } from "../components/thread/TaskThreadViewport"
import { readThreadScrollMetrics, stableThreadScrollDelta, type ThreadScrollMetrics } from "../components/thread/threadScrollIntent"
import { TaskTraySurface } from "../components/tray/TaskTraySurface"
import { TaskDockedTray } from "../components/tray/TaskDockedTray"
import { DockedPaneResizable } from "../components/tray/DockedPaneResizable"
import { codeSidebarManager } from "../components/sidebar/sidebarManager"
import { QueueModeLaunch } from "../components/sidebar/QueueModeLaunch"
import { Z_INDEX } from "../constants"
import { useImageDropZone } from "../hooks/useImageDropZone"
import { useMediaQuery } from "../hooks/useMediaQuery"
import { isProductModuleTaskSurfaceVisible, productModuleRightPaneID, productModuleTaskLifecycleControls } from "../modules/registry"
import { SessionThreadModel } from "../store/SessionThreadModel"
import type { TaskModel } from "../store/TaskModel"
import { useCodeStore } from "../store/context"
import type { NestTaskRuntimeRecord } from "../store/managers/RuntimeManager"
import { dockedPaneWidthStore } from "../store/managers/dockedPaneWidthStore"
import type { TaskTimelineNavigationTarget } from "../timeline/taskTimelineTypes"
import { isEventFromTerminal } from "../utils/keyboardShortcuts"

interface TaskPageProps {
    workspaceId: string
    taskId: string
    taskModel: TaskModel
    onSlept?: () => void
    onComposerInput?: () => void
    onStopRequested?: () => void
    onWorkDispatchStarted?: () => void
    onWorkDispatched?: () => void
    workspaceSidebarOpen?: boolean
    onCloseWorkspaceSidebar?: () => void
    onNavbarVisibilityChange?: (visible: boolean) => void
    queueControl?: ReactNode
    queueInputControl?: ReactNode
    queueHandoff?: ReactNode
}

interface TaskThreadPaneProps {
    taskId: string
    taskModel: TaskModel
    sessionThreadModel: SessionThreadModel | null
    renderSessionThread: boolean
    taskRuntime: NestTaskRuntimeRecord | null
    scrollViewportRef: RefObject<HTMLDivElement>
    pendingTaskOpenScrollRef: MutableRefObject<string | null>
    stickToBottomRef: MutableRefObject<boolean>
    onScroll: UIEventHandler<HTMLDivElement>
    scrollToBottom: () => void
    timelineNavigationTarget?: TaskTimelineNavigationTarget | null
}

const TaskThreadPane = observer(function TaskThreadPane({
    taskId,
    taskModel,
    sessionThreadModel,
    renderSessionThread,
    taskRuntime,
    scrollViewportRef,
    pendingTaskOpenScrollRef,
    stickToBottomRef,
    onScroll,
    scrollToBottom,
    timelineNavigationTarget,
}: TaskThreadPaneProps) {
    const liveThreadSession = sessionThreadModel?.session
    const events = renderSessionThread ? [] : taskModel.activityEvents
    const taskActivity = useMemo(
        () =>
            taskRuntime
                ? {
                      id: taskRuntime.turnId,
                      status: taskRuntime.status,
                      startedAt: taskRuntime.startedAt,
                  }
                : null,
        [taskRuntime?.startedAt, taskRuntime?.status, taskRuntime?.turnId]
    )

    useLayoutEffect(() => {
        stickToBottomRef.current = true
        if (renderSessionThread) {
            pendingTaskOpenScrollRef.current = null
            return
        }
        pendingTaskOpenScrollRef.current = taskId
        scrollToBottom()
    }, [pendingTaskOpenScrollRef, renderSessionThread, scrollToBottom, stickToBottomRef, taskId])

    useLayoutEffect(() => {
        if (renderSessionThread) return
        if (pendingTaskOpenScrollRef.current !== taskId) return
        if (!taskModel.dynamicActivityReady) return

        scrollToBottom()
        pendingTaskOpenScrollRef.current = null
    }, [pendingTaskOpenScrollRef, renderSessionThread, scrollToBottom, taskId, taskModel.dynamicActivityReady])

    useLayoutEffect(() => {
        if (events.length > 0 && stickToBottomRef.current) scrollToBottom()
    }, [events.length, scrollToBottom, stickToBottomRef])

    useEffect(() => {
        if (!renderSessionThread || !liveThreadSession) return
        taskModel.syncExecutionSelectionFromSession(liveThreadSession)
    }, [liveThreadSession, renderSessionThread, taskModel])

    return (
        <TaskThreadViewport viewportRef={scrollViewportRef} onScroll={onScroll}>
            {renderSessionThread && sessionThreadModel ? (
                <>
                    {events.length > 0 && <EventLog taskId={taskId} events={events} />}
                    <TaskHandoffHistory taskModel={taskModel} />
                    <SessionThreadView
                        key={`${sessionThreadModel.reference.sessionId}:${sessionThreadModel.reference.harnessId}`}
                        model={sessionThreadModel}
                        taskId={taskId}
                        taskSessions={taskModel.sessionDebugInfo}
                        activity={taskActivity}
                        scrollElementRef={scrollViewportRef}
                        initialScrollToBottom
                        timelineNavigationTarget={timelineNavigationTarget}
                    />
                </>
            ) : (
                <EventLog taskId={taskId} events={events} loading={taskModel.dynamicActivityLoading} error={taskModel.dynamicActivityError} />
            )}
            {!renderSessionThread && taskActivity && isActiveThreadStatus(taskActivity.status) && <ThreadActivityBar activity={taskActivity} />}
        </TaskThreadViewport>
    )
})

export const TaskPage = observer((props: TaskPageProps) => {
    const {
        workspaceId,
        taskId,
        taskModel,
        onSlept,
        onComposerInput,
        onStopRequested,
        onWorkDispatchStarted,
        onWorkDispatched,
        workspaceSidebarOpen = false,
        onCloseWorkspaceSidebar,
        onNavbarVisibilityChange,
        queueControl,
        queueInputControl,
        queueHandoff,
    } = props
    const codeStore = useCodeStore()
    const scrollViewportRef = useRef<HTMLDivElement>(null)
    const pendingTaskOpenScrollRef = useRef<string | null>(null)
    const stickToBottomRef = useRef(true)
    const lastScrollMetricsRef = useRef<ThreadScrollMetrics | null>(null)
    const [showScrollToBottom, setShowScrollToBottom] = useState(false)
    const [workspaceSidebarExpanded, setWorkspaceSidebarExpanded] = useState(true)
    const [timelineNavigation, setTimelineNavigation] = useState<{ taskId: string; target: TaskTimelineNavigationTarget } | null>(null)
    const { input, tray } = taskModel
    const compact = useMediaQuery("(max-width: 719px)")
    const dockedTrayOpen = tray.isDocked && !compact
    const moduleTaskSurfaceVisible = isProductModuleTaskSurfaceVisible()
    const moduleRightPaneID = compact ? null : productModuleRightPaneID()
    const moduleRightPaneDocked = moduleRightPaneID !== null
    const workspaceSidebarInteractionProps = dockedTrayOpen || moduleRightPaneDocked ? { inert: "" } : {}

    useEffect(() => {
        if (moduleTaskSurfaceVisible && tray.isOpen) tray.close()
    }, [moduleTaskSurfaceVisible, tray])

    useEffect(() => {
        codeSidebarManager.setTaskPaneDocked(dockedTrayOpen)
    }, [dockedTrayOpen])

    useEffect(
        () => () => {
            codeSidebarManager.setTaskPaneDocked(false)
        },
        []
    )

    const scrollToBottom = useCallback(() => {
        const viewport = scrollViewportRef.current
        if (!viewport) return
        viewport.scrollTop = viewport.scrollHeight
        lastScrollMetricsRef.current = readThreadScrollMetrics(viewport)
        stickToBottomRef.current = true
        setShowScrollToBottom(false)
    }, [])
    const handleWorkDispatchStarted = useCallback(() => {
        scrollToBottom()
        onWorkDispatchStarted?.()
    }, [onWorkDispatchStarted, scrollToBottom])
    const handleScroll = useCallback(() => {
        const viewport = scrollViewportRef.current
        if (!viewport) return
        const nextMetrics = readThreadScrollMetrics(viewport)
        const atBottom = nextMetrics.scrollHeight - nextMetrics.scrollTop - nextMetrics.clientHeight <= THREAD_VIEWPORT_LIVE_EDGE_THRESHOLD_PX
        const scrollDelta = stableThreadScrollDelta(lastScrollMetricsRef.current, nextMetrics)
        const followingLiveEdge = stickToBottomRef.current && atBottom
        if (!followingLiveEdge) {
            if (nextMetrics.scrollTop <= 24) onNavbarVisibilityChange?.(true)
            else if (scrollDelta !== null && scrollDelta > 6) onNavbarVisibilityChange?.(false)
            else if (scrollDelta !== null && scrollDelta < -6) onNavbarVisibilityChange?.(true)
        }
        lastScrollMetricsRef.current = nextMetrics
        stickToBottomRef.current = atBottom
        setShowScrollToBottom(!atBottom)
        if (!atBottom) pendingTaskOpenScrollRef.current = null
    }, [onNavbarVisibilityChange])

    useEffect(() => {
        lastScrollMetricsRef.current = null
        onNavbarVisibilityChange?.(true)
    }, [onNavbarVisibilityChange, taskId])

    useEffect(() => setTimelineNavigation(null), [taskId])

    useHotkeys("escape", () => tray.close(), {
        enabled: tray.isOpen,
        enableOnContentEditable: true,
        enableOnFormTags: true,
        ignoreEventWhen: isEventFromTerminal,
    })

    const sessionRefreshKey = taskModel.statsRefreshKey
    useEffect(() => {
        void taskModel.loadTaskSessionManifest()
    }, [taskModel])

    useEffect(() => {
        if (tray.openTray !== "sessions") return
        void Promise.all([taskModel.loadTaskSessionManifest(), taskModel.loadNestUsage()])
    }, [sessionRefreshKey, taskModel, tray.openTray])

    const threadReference = taskModel.primarySessionThreadReference
    const threadKey = threadReference ? `${threadReference.sessionId}:${threadReference.harnessId}` : ""
    // threadKey owns SessionThreadModel lifetime across equivalent reference objects.
    const sessionThreadModel = useMemo(
        () =>
            threadReference
                ? new SessionThreadModel(threadReference, {
                      onProviderWorkingDirectoryChange: async (path) => {
                          const error = await taskModel.setRendererWorkingDirectory(path)
                          if (error) throw new Error(error)
                      },
                  })
                : null,
        [taskModel, threadKey]
    )
    const renderSessionThread = taskModel.shouldRenderSessionThread && sessionThreadModel !== null
    const taskRuntime = taskModel.isWorking ? codeStore.runtimes.activeRuntimeForTask(taskId) : null
    const taskLastActivityAt = codeStore.tasks.getSidebarTasksForRepo(workspaceId).find((task) => task.id === taskId)?.lastActivityAt

    // Get SmartEditorManager for this task
    const editorManager = codeStore.smartEditors.getManager(`task-${taskId}`, workspaceId)

    // Mark task as viewed when navigating to it or while new activity arrives on the open task.
    useEffect(() => {
        codeStore.tasks.markTaskViewed(taskId, taskLastActivityAt)
    }, [codeStore, taskId, taskLastActivityAt])

    // Resolve the task's working directory and Git state.
    useEffect(() => {
        void taskModel.refreshDirectoryView()
    }, [taskModel])

    useEffect(() => {
        // Legacy event-mode tasks still need dynamic session hydration; migrated
        // Nest tasks render the session thread directly from `TaskRecord.sessions`.
        if (!renderSessionThread) taskModel.loadDynamicActivity()
    }, [taskModel, renderSessionThread])

    useEffect(() => {
        const refreshGitState = () => {
            void taskModel.refreshDirectoryView().then(() => taskModel.refreshCheckoutSummaries())
        }
        const handleBlur = () => {
            refreshGitState()
        }
        const handleFocus = () => {
            refreshGitState()
            if (!renderSessionThread) {
                taskModel.loadDynamicActivity()
                return
            }
            void sessionThreadModel?.refreshFromNest()
        }
        window.addEventListener("blur", handleBlur)
        window.addEventListener("focus", handleFocus)
        return () => {
            window.removeEventListener("blur", handleBlur)
            window.removeEventListener("focus", handleFocus)
        }
    }, [renderSessionThread, sessionThreadModel, taskModel])

    // Poll while the agent is active so every directory-backed surface follows target changes.
    useEffect(() => {
        if (!taskModel.isWorking) return

        const interval = setInterval(() => {
            void taskModel.refreshDirectoryView()
            if (!renderSessionThread) taskModel.loadDynamicActivity()
        }, 20_000)

        return () => clearInterval(interval)
    }, [taskModel, taskModel.isWorking, renderSessionThread])

    // Colleagues' suggestions follow the open task only; the store point-reads
    // the record and subscribes to its changes while this page is mounted.
    useEffect(() => codeStore.proposals.watch(taskId, workspaceId), [codeStore, taskId, workspaceId])

    const workingDirectory = taskModel.getDirectory("working")
    const worktreeControl = taskWorktreeControlForModel(taskModel)
    const hideWorkspaceTools = codeStore.personalSettingsStore?.settings.current.devHideTray
    const loadCheckoutSummaries = useCallback(() => {
        void taskModel.loadCheckoutSummaries(true)
    }, [taskModel])

    // Page-level drop zone for images
    const { isDragOver, dragHandlers } = useImageDropZone(editorManager)
    const workspaceSidebar = (
        <TaskWorkspaceSidebar
            taskId={taskId}
            workspaceId={workspaceId}
            tray={tray}
            taskModel={taskModel}
            gitStatus={taskModel.displayGitStatus}
            pullRequest={taskModel.pullRequest}
            worktree={worktreeControl}
            onDirectoryChange={(path) => taskModel.setWorkingDirectory(path)}
            onCheckoutSummariesOpen={loadCheckoutSummaries}
            showTestFiles={codeStore.showTestFiles}
            enabledMcpServerIds={taskModel.enabledMcpServerIds}
            onMcpServerIdsChange={(ids) => taskModel.setEnabledMcpServerIds(ids)}
            mobileOpen={workspaceSidebarOpen}
            onMobileClose={() => onCloseWorkspaceSidebar?.()}
            hideWorkspaceTools={hideWorkspaceTools}
            desktopContained={!compact}
            onExpandedChange={setWorkspaceSidebarExpanded}
            onTimelineNavigate={(target) => setTimelineNavigation({ taskId, target })}
        />
    )

    return (
        <div
            className="relative flex h-full min-w-0 flex-col"
            style={tray.isModalOpen ? { zIndex: Z_INDEX.TASK_PAGE_TRAY_CONTEXT } : undefined}
            data-task-page="true"
        >
            <div className="relative flex min-h-0 min-w-0 flex-1">
                <div className="@container relative flex min-w-0 flex-1 flex-col" data-task-conversation-pane="true">
                    {queueControl ? (
                        <div className="relative flex-none" style={{ zIndex: Z_INDEX.QUEUE_HUD }} data-queue-control-lane="true">
                            {queueControl}
                        </div>
                    ) : (
                        <TaskNavbarSlot />
                    )}
                    {queueHandoff && (
                        <div className="flex-none" data-queue-handoff-lane="true">
                            {queueHandoff}
                        </div>
                    )}
                    <div className="relative min-h-0 min-w-0 flex-1" {...dragHandlers}>
                        {isDragOver && <ImageDropOverlay />}
                        <TaskThreadPane
                            taskId={taskId}
                            taskModel={taskModel}
                            sessionThreadModel={sessionThreadModel}
                            renderSessionThread={renderSessionThread}
                            taskRuntime={taskRuntime}
                            scrollViewportRef={scrollViewportRef}
                            pendingTaskOpenScrollRef={pendingTaskOpenScrollRef}
                            stickToBottomRef={stickToBottomRef}
                            onScroll={handleScroll}
                            scrollToBottom={scrollToBottom}
                            timelineNavigationTarget={timelineNavigation?.taskId === taskId ? timelineNavigation.target : null}
                        />
                        {codeStore.canOperate === false ? (
                            <GuestComposerNotice taskId={taskId} workspaceId={workspaceId} />
                        ) : (
                            <InputBar
                                input={input}
                                editorManager={editorManager}
                                tray={tray}
                                fileMentionsDir={workingDirectory?.path ?? null}
                                fileMentionsContext={workingDirectory?.context ?? null}
                                slashCommandsDir={workingDirectory?.path ?? null}
                                sdkCapabilities={taskModel.sdkCapabilities}
                                unsubmittedComments={codeStore.comments.getUnsubmittedComments(taskId)}
                                onDeleteComment={(commentId) => codeStore.comments.removeComment(taskId, commentId)}
                                gitStatus={taskModel.displayGitStatus}
                                taskCommitCount={taskModel.taskCommitCount}
                                pullRequest={taskModel.pullRequest}
                                selectedModel={taskModel.model}
                                onModelChange={(model) => taskModel.setModel(model)}
                                delegateTarget={taskModel.delegateTarget}
                                onDelegateChange={(target) => taskModel.setDelegateTarget(target)}
                                thinking={taskModel.thinking}
                                onThinkingChange={(thinking) => taskModel.setThinking(thinking)}
                                fastMode={taskModel.fastMode}
                                onFastModeChange={(enabled) => taskModel.setFastMode(enabled)}
                                harnessId={taskModel.harnessId}
                                pinnedAgentTargets={codeStore.personalSettingsStore?.settings.current.newTaskPinnedAgents}
                                hideTray={hideWorkspaceTools}
                                autoFocusKey={taskId}
                                onManageActions={() => {
                                    void NiceModal.show(SettingsModal, {
                                        store: codeStore,
                                        initialTab: "actions",
                                    })
                                }}
                                showScrollToBottom={showScrollToBottom}
                                onScrollToBottom={scrollToBottom}
                                taskHandoffControl={
                                    <TaskHandoffButton
                                        taskModel={taskModel}
                                        pinnedTargets={codeStore.personalSettingsStore?.settings.current.newTaskPinnedAgents}
                                        onStarted={onWorkDispatchStarted}
                                        onAccepted={onWorkDispatched}
                                    />
                                }
                                taskLifecycleControl={
                                    queueInputControl ? (
                                        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
                                            {productModuleTaskLifecycleControls({ taskModel, onSlept })}
                                            {queueInputControl}
                                        </div>
                                    ) : (
                                        productModuleTaskLifecycleControls({ taskModel, onSlept })
                                    )
                                }
                                onComposerInput={onComposerInput}
                                onStopRequested={onStopRequested}
                                onWorkDispatchStarted={handleWorkDispatchStarted}
                                onWorkDispatched={onWorkDispatched}
                            />
                        )}
                        <TaskTraySurface tray={tray} workspaceSidebarExpanded={workspaceSidebarExpanded} />
                    </div>
                    <QueueModeLaunch />
                </div>
                {compact ? (
                    workspaceSidebar
                ) : (
                    <div
                        className={`relative min-h-0 shrink-0 overflow-hidden ease-in-out motion-reduce:transition-none ${
                            dockedPaneWidthStore.isResizing ? "transition-none" : "transition-[width] duration-300"
                        }`}
                        style={{
                            width: moduleRightPaneDocked ? 0 : dockedTrayOpen ? dockedPaneWidthStore.cssWidth : workspaceSidebarExpanded ? "18rem" : "3rem",
                        }}
                        data-task-right-pane="true"
                        data-presentation={moduleRightPaneID ?? (dockedTrayOpen ? "docked" : "workspace")}
                    >
                        <div
                            className={`absolute inset-0 transition-[transform,opacity] duration-300 ease-in-out motion-reduce:transition-none ${
                                dockedTrayOpen || moduleRightPaneDocked
                                    ? "invisible pointer-events-none translate-x-full opacity-0"
                                    : "visible translate-x-0 opacity-100"
                            }`}
                            aria-hidden={dockedTrayOpen || moduleRightPaneDocked}
                            {...workspaceSidebarInteractionProps}
                            data-task-workspace-sidebar-layer="true"
                        >
                            {workspaceSidebar}
                        </div>
                        {dockedTrayOpen && (
                            <DockedPaneResizable>
                                <TaskDockedTray tray={tray} />
                            </DockedPaneResizable>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
})
